import { solid, regular } from '@fortawesome/fontawesome-svg-core/import.macro'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { memo, useCallback, useEffect, useState } from 'react'
import { Alert, Container, Modal, Button, Row, Col, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router';
import { Datasets } from '../../api/Datasets';
import { VizClient } from '../../api/VizClient';
import AppLoader from '../../components/common/AppLoader';
import { TailSpin } from "react-loader-spinner";
import AssetItem from '../../components/list-items/AssetItem';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { useToasts } from '../../contexts/toasts';
import { accessLevelOptions, getConnectionDetails, createQueryDetails } from '../../utils';
import { Select } from "antd";

import windowsLogo from '../../assets/images/windows_logo.png';
import linuxLogo from '../../assets/images/linux_logo.png';
import macLogo from '../../assets/images/mac_logo.png';
import { BdaDataset } from '../../models/BdaDataset';
import { PagedResults } from '../../models/generics';
import AppBrowsingToolbar, { Filters } from '../../components/common/AppBrowsingToolbar';
import { DefaultOptionType } from 'antd/es/select';
import { EventEmitter } from '../../utils/EventEmitter';

const statusOptions = [
  { label: "Virtual", value: "false", icon: solid("lock") },
  { label: "Created", value: "true", icon: regular("check-circle") },
];

const datasetsListFilters: Filters = {
  'search': {
    placeholder: 'Search dataset...',
    query: 'freeText',
  },
  'created': {
    placeholder: 'Select status',
    select: true,
    query: 'created',
    type: 'string',
    options: statusOptions,
    customOptions: true,
    renderOption: (opt: DefaultOptionType, index: number) => (
      <Select.Option key={index} value={opt.value} label={opt.label}>
        <div>
          <FontAwesomeIcon icon={opt.icon} fixedWidth />
          <span className="ms-2">{opt.label}</span>
        </div>
      </Select.Option>
    )
  },
  'accessLevel': {
    placeholder: 'Select privacy level',
    select: true,
    query: 'accessLevel',
    type: 'string',
    options: accessLevelOptions
      .filter(option => window.__env.ALLOWED_ACCESS_LEVELS ? window.__env.ALLOWED_ACCESS_LEVELS.includes(option.value) : true),
    customOptions: true,
    renderOption: (option: DefaultOptionType, index: number) => (
      <Select.Option key={index} value={option.value} label={option.label}>
        <div>
          <FontAwesomeIcon icon={option.icon} fixedWidth />
          <span className="ms-2">{option.label}</span>
        </div>
      </Select.Option>
    )
  },
  'owned': {
    label: 'My Datasets',
    checkbox: true,
    type: 'boolean',
    query: 'createdBy'
  }
}

function DatasetsList() {
  const [datasets, setDatasets] = useState<BdaDataset[]>();
  const [totalDatasets, setTotalDatasets] = useState<number>();
  const [error, setError] = useState(false);
  const [latestParams, setLatestParams] = useState<URLSearchParams>();

  const [datasetToDelete, setDatasetToDelete] = useState<BdaDataset>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [showDownloadModal, setShowDownloadModal] = useState(false);

  const [datasetPreview, setDatasetPreview] = useState<any[]>();
  const [show, setShow] = useState(false);

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();
  const navigate = useNavigate();

  const getDatasets = useCallback((queryParams?: Record<string, any>) => {
    const params = new URLSearchParams(queryParams || latestParams);
    if (queryParams) setLatestParams(params);

    Datasets.getDatasets(params)
      .then(
        ({ data: datasetPages }) => {
          setDatasets((datasetPages as PagedResults<BdaDataset>).content);
          setTotalDatasets((datasetPages as PagedResults<BdaDataset>).pageMetadata.totalElements);
        },
        (err) => {
          showToast('datasets-error', 'Could not get datasets', 'Datasets Error', 'danger');
          setDatasets([]);
          setError(true);
        }
      );
  }, [showToast, latestParams]);

  const reset = useCallback((params?: Record<string, any>) => {
    setError(false);
    setDatasets(undefined);
    getDatasets(params);
  }, [getDatasets]);

  useEffect(() => {
    EventEmitter.subscribe('browsing:paramsChanged', (params: any) => {
      reset(params);
    });

    return () => EventEmitter.unsubscribe('browsing:paramsChanged');
  }, [reset]);

  const handleDeleteClose = () => {
    setShowDeleteModal(false);
    setDatasetToDelete(undefined);
  };
  const handleClose = () => {
    setShow(false);
    setDatasetPreview(undefined);
  };
  const handleDownloadClose = () => setShowDownloadModal(false);

  const navigateToCreation = () => navigate('/datasets/create');

  const editDataset = (dataset: BdaDataset) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    navigate(`/datasets/${dataset.id}/edit`);
  }

  const onDeleteDataset = (dataset: BdaDataset) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    setShowDeleteModal(true);
    setDatasetToDelete(dataset);
  }

  const deleteDataset = () => {
    showLoader();

    Datasets.deleteDataset(datasetToDelete!.id!)
      .then(
        () => {
          showToast('dataset-delete-success', 'Dataset deleted successfully.', 'Success', 'success');
          hideLoader();
          reset();
          handleDeleteClose();
        },
        () => {
          hideLoader();
          showToast('datasets-error', 'Could not delete dataset', 'Dataset Error', 'danger');
        }
      )
  }

  const datasetDetail = (dataset: BdaDataset) => () => navigate(`/datasets/${dataset.id}`);

  const openDatasetPreview = (dataset: BdaDataset) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    setShow(true);

    const connectionDetails = getConnectionDetails(dataset.datasource!, undefined, dataset.datasetFileType);
    const body = createQueryDetails(connectionDetails);
    VizClient.getDatasetPreview(body, connectionDetails.type)
      .then(
        ({ data: preview }) => setDatasetPreview(preview?.result?.data || []),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('dataset-error', message, `Dataset Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const openDownloadModal = () => setShowDownloadModal(true);

  const downloadClient = (distribution: string) => () => {
    const cdnUrl = `${window.__env.ALIDA_CDN}/releases/ioclient/${distribution}/AlidaIOClient${distribution === "windows" ? '.exe' : ''}`;
    window.open(cdnUrl, "_blank");
  }

  const renderPreview = () => {
    if (!datasetPreview) return (
      <div className="d-flex justify-content-center py-3">
        <AppLoader size={250} />
      </div>
    )

    return datasetPreview.length > 0
      ? (
        <div className="table-wrapper">
          <Table className="mb-0" bordered>
            <thead>
              <tr>
                {Object.keys(datasetPreview[0]).map(key => (
                  <th key={key}>{key}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {datasetPreview.map((row, i) => (
                <tr key={i}>
                  {Object.keys(row).map((col, j) => (
                    <td key={j}>{row[col]}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )
      : (
        <Alert className="p-2 mb-0">
          <FontAwesomeIcon icon={solid('info-circle')} />
          <span className="ms-2">Dataset is empty.</span>
        </Alert>
      )
  }

  const renderDatasets = () => {
    if (error) return (
      <Alert variant="danger" className="py-2">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occured, please try again later.</span>
      </Alert>
    );

    return datasets!.length === 0
      ? (
        <Alert variant="info" className="py-2">
          <FontAwesomeIcon icon={solid('info-circle')} />
          <span className="ms-2">No datasets found.</span>
        </Alert>
      )
      : (
        <Row>
          {
            datasets!.map(dataset => (
              <Col xs={12} sm={6} md={4} lg={3} className="p-2" key={dataset.id}>
                <AssetItem
                  type="dataset"
                  asset={dataset}
                  onEdit={editDataset(dataset)}
                  onDelete={onDeleteDataset(dataset)}
                  onPreview={openDatasetPreview(dataset)}
                  onClick={datasetDetail(dataset)}
                />
              </Col>
            ))
          }
        </Row>
      )
  }

  const renderAsyncContent = () => {
    return datasets
      ? renderDatasets()
      : (
        <div className="d-flex justify-content-center py-5 my-5">
          <TailSpin
            height={100}
            width={100}
            color="#677ab1"
            radius="1"
          />
        </div>
      );
  }

  return (
    <Container fluid="md" className="mt-3">
      <AppBrowsingToolbar
        title="Datasets List"
        actions={
          <Col xs={6} md="auto" className="text-end">
            <Button size="sm" variant="light" className="me-2 border text-primary" onClick={openDownloadModal}>
              <FontAwesomeIcon icon={solid('file-download')} />
              <span className="d-none d-sm-inline ms-2">Download I/O Client</span>
            </Button>
            <Button size="sm" variant="success" onClick={navigateToCreation}>
              <FontAwesomeIcon icon={solid('plus')} />
              <span className="d-none d-sm-inline ms-2">Register Dataset</span>
            </Button>
          </Col>
        }
        filters={datasetsListFilters}
        total={totalDatasets}
      />

      <hr />

      {renderAsyncContent()}

      <Modal size="lg" show={show} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Dataset Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>{renderPreview()}</Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleClose}>Close</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDownloadModal} onHide={handleDownloadClose}>
        <Modal.Header closeButton>
          <Modal.Title>Download I/O Client</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <p>Choose your distribution below:</p>

          <Row className="justify-content-center mb-3">
            <Col md={4}>
              <Button variant="light" className="p-3 w-100 border" onClick={downloadClient('windows')}>
                <img src={windowsLogo} height={75} alt="Windows distribution" />
                <h6 className="mt-3 mb-0">Windows</h6>
              </Button>
            </Col>
            <Col md={4}>
              <Button variant="light" className="p-3 w-100 border" onClick={downloadClient('mac')}>
                <img src={macLogo} height={75} alt="MacOS distribution" />
                <h6 className="mt-3 mb-0">Mac OS</h6>
              </Button>
            </Col>
            <Col md={4}>
              <Button variant="light" className="p-3 w-100 border" onClick={downloadClient('linux')}>
                <img src={linuxLogo} height={75} alt="Linux distribution" />
                <h6 className="mt-3 mb-0">Linux</h6>
              </Button>
            </Col>
          </Row>
        </Modal.Body>
      </Modal>

      <Modal show={showDeleteModal} onHide={handleDeleteClose}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Dataset</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete dataset <b>{datasetToDelete && datasetToDelete.name}</b>?</p>

          <Alert variant="warning">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Remember, this operation cannot be undone.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleDeleteClose}>
            Close
          </Button>
          <Button variant="danger" size="sm" onClick={deleteDataset}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}

export default memo(DatasetsList);
